/**
 * Landing Page - Main public-facing marketing page
 *
 * Version History:
 * v4.38 - 2026-10-29 - EXPO-QUALIFIER-01 offer strip (Branko 2026-10-29): OfferStrip rendered first, directly under the
 *         site header (never above it); hidden when the offer is closed or the server cannot answer.
 * v4.08 - AEO-08: Add "What is MyLojic?" H2 block for AI crawler indexing - 2026-04-11
 * v4.07 - Move "Why This Exists" inside hero section, remove standalone component - 2026-04-09
 * v4.06 - Add "Why This Exists" section between Hero and Problem - 2026-04-09
 * v4.05 - PORTAL-006: Full landing page copy rewrite — hero 4-line sequence, sound familiar, how it works, value section, final CTA - 2026-03-27
 * v4.01 - Copy: pain point 2 rewrite; How It Works 2 taglines; founder quote reframe; jargon audit; bg lift - 2026-03-20
 * v4.00 - IndustryStats: revert to 3-tile frosted-glass layout; Hero: random insight tile replaces bar chart - 2026-03-20
 * v3.99 - IndustryStats: single randomised insight example, 5 industry templates, selected on mount - 2026-03-20
 * v3.97 - Add IndustryStats section (frosted glass tiles) between How It Works and Founding Story - 2026-03-20
 * v3.96 - Copy fixes: meta desc, hero subhead, final CTA subhead, How It Works cap, remove dupe CTA - 2026-03-20
 * v4.10 - 2026-06-10 - STORY-10E-C: PricingSection fully catalog-driven; no price/tier literals; omit elements when absent
 * v4.09 - 2026-06-05 - STORY8-STEP3: PricingSection reads price/limits from /api/plans catalog
 * v3.94 - Full redesign: mode-aware sections, F-01 through S-07, X-03 tracking - 2026-03-20
 *   Sections: Hero (S-01), Problem (S-02), HowItWorks (S-03), SocialProof (S-04),
 *             Pricing (S-05, flag-gated), FounderStory (S-06), FinalCTA (S-07)
 * v3.85 - MKTG-02: Non-US waitlist banner - 2026-03-16
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  Divider,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import LooksTwoIcon from '@mui/icons-material/LooksTwo';
import Looks3Icon from '@mui/icons-material/Looks3';
import NonUsWaitlistBanner from '../components/NonUsWaitlistBanner';
import ChatDemoTile from '../components/ChatDemoTile';
import OfferStrip from '../components/OfferStrip'; // v4.38 - EXPO-QUALIFIER-01 offer strip
import { useFeatureFlags, getPrimaryCTA } from '../contexts/FeatureFlagContext';
import { analytics } from '../utils/analytics';
import { plansService, Tier } from '../services/api';
// v4.10 - STORY-10E-C: catalog helpers replace hardcoded enterprise lookup
import { findUpgradeTier } from '../utils/planCatalog';
// v4.04 - heroExamples removed — migrated to src/data/chatDemos.ts, rendered via ChatDemoTile - 2026-03-23

// ─── S-01: HERO ───────────────────────────────────────────────────────────────
// v4.05 - PORTAL-006: 4-line headline sequence, light bg, line 4 fades in at 300ms - 2026-03-27
const HeroSection: React.FC<{ siteMode: string; flagsLoaded: boolean }> = ({ siteMode, flagsLoaded }) => {
  const navigate = useNavigate();
  const [showLine4, setShowLine4] = useState(false);
  const cta = getPrimaryCTA(siteMode as any);

  useEffect(() => {
    const timer = setTimeout(() => setShowLine4(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const handleCTAClick = () => {
    analytics.ctaClick('hero', siteMode);
    navigate(cta.href);
  };

  return (
    // v4.05 - Light background: lines 1-3 (#666) and line 4 (#000) require light bg to be readable
    <Box sx={{ bgcolor: 'background.paper', pt: { xs: 6, md: 10 }, pb: { xs: 4, md: 6 } }}>
      <Container maxWidth="lg">
        <Grid container spacing={6} alignItems="center">
          {/* Left: 4-line headline + subtext + CTA */}
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h1" sx={{ color: '#666', fontWeight: 400, fontSize: { xs: '1.6rem', sm: '2rem', md: '2.4rem' }, lineHeight: 1.6, mb: 0 }}>
                Your accounting shows revenue.
              </Typography>
              <Typography variant="h1" sx={{ color: '#666', fontWeight: 400, fontSize: { xs: '1.6rem', sm: '2rem', md: '2.4rem' }, lineHeight: 1.6, mb: 0 }}>
                Your payroll shows costs.
              </Typography>
              <Typography variant="h1" sx={{ color: '#666', fontWeight: 400, fontSize: { xs: '1.6rem', sm: '2rem', md: '2.4rem' }, lineHeight: 1.6, mb: 1 }}>
                Your spreadsheets are everywhere.
              </Typography>
              {/* Line 4: always in DOM (no layout shift), fades in at 300ms */}
              <Typography
                variant="h1"
                sx={{
                  color: '#000',
                  fontWeight: 700,
                  fontSize: { xs: '1.6rem', sm: '2rem', md: '2.4rem' },
                  lineHeight: 1.6,
                  opacity: showLine4 ? 1 : 0,
                  transform: showLine4 ? 'translateY(0)' : 'translateY(10px)',
                  transition: 'opacity 250ms ease-out, transform 250ms ease-out',
                }}
              >
                MyLojic shows what actually makes you money.
              </Typography>
            </Box>

            <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary', fontWeight: 400, lineHeight: 1.6, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              See what your business is really doing, across all your data, in one place.
            </Typography>

            {/* CTA */}
            {flagsLoaded && (
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { sm: 'center' } }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleCTAClick}
                  fullWidth={false}
                  sx={{
                    bgcolor: 'accent.main',
                    color: 'accent.contrastText',
                    py: 1.75,
                    px: 5,
                    fontSize: '1rem',
                    fontWeight: 700,
                    boxShadow: 3,
                    '&:hover': { bgcolor: 'accent.dark', boxShadow: 5 },
                  }}
                >
                  {cta.text}
                </Button>

                {/* Open mode only: trust micro-copy */}
                {siteMode === 'open' && (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No credit card. No setup. No BI team.
                  </Typography>
                )}
              </Box>
            )}

            {/* Why this exists — v4.07 moved inside hero */}
            <Box sx={{ mt: 6 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}
              >
                Why this exists
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: 'text.secondary', lineHeight: 1.8 }}
              >
                Many owner‑operated businesses work hard, review their numbers regularly, and still feel
                something is off until it shows up as a late or costly problem.
              </Typography>
            </Box>
          </Grid>

          {/* Right: dark wrapper preserves frosted-glass appearance — DO NOT TOUCH ChatDemoTile */}
          <Grid item xs={12} md={6}>
            <Box sx={{ bgcolor: 'secondary.main', borderRadius: '20px', p: { xs: 3, md: 4 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ChatDemoTile />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

// ─── S-02: PROBLEM ────────────────────────────────────────────────────────────
// v4.05 - PORTAL-006: new header, removed subhead, 3 new problem cards - 2026-03-27
const ProblemSection: React.FC = () => {
  const problems = [
    {
      headline: 'Your data is everywhere',
      body: 'QuickBooks, spreadsheets, Stripe, Shopify. But nothing connects it.',
    },
    {
      headline: "You don't see what actually drives profit",
      body: 'Revenue looks fine. But costs, time, and operations tell a different story.',
    },
    {
      headline: 'Getting answers takes too long',
      body: 'By the time the answer comes, the moment is gone.',
    },
  ];

  return (
    <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'background.default' }}>
      <Container maxWidth="lg">
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, mb: 5, textAlign: 'center', color: 'text.primary' }}
        >
          You're making decisions without the full picture
        </Typography>

        <Grid container spacing={3}>
          {problems.map((p, i) => (
            <Grid item xs={12} md={4} key={i}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  transition: 'border-color 0.2s',
                  '&:hover': { borderColor: 'primary.main' },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary', lineHeight: 1.4 }}>
                    {p.headline}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    {p.body}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

// ─── S-03: HOW IT WORKS ───────────────────────────────────────────────────────
// v4.05 - PORTAL-006: new header, removed subhead, new step copy, single bottom line - 2026-03-27
const HowItWorksSection: React.FC = () => {
  const steps = [
    {
      icon: <LooksOneIcon sx={{ fontSize: 40, color: 'accent.main' }} />,
      title: 'Connect your tools',
      body: 'QuickBooks, Xero, Stripe, Shopify, spreadsheets. Everything in one place.',
    },
    {
      icon: <LooksTwoIcon sx={{ fontSize: 40, color: 'accent.main' }} />,
      title: 'Ask a question',
      body: 'Which jobs are actually profitable? Where am I overspending? Which customers cost me money?',
    },
    {
      icon: <Looks3Icon sx={{ fontSize: 40, color: 'accent.main' }} />,
      title: 'Get the real answer',
      body: 'Across all your data. Not just one system.',
    },
  ];

  return (
    <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'background.paper' }}>
      <Container maxWidth="lg">
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, mb: 5, textAlign: 'center', color: 'text.primary' }}
        >
          From scattered data to clear answers
        </Typography>

        <Grid container spacing={4}>
          {steps.map((step, i) => (
            <Grid item xs={12} md={4} key={i}>
              <Box sx={{ textAlign: 'center', px: { md: 2 } }}>
                <Box sx={{ mb: 2 }}>{step.icon}</Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
                  {step.title}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                  {step.body}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ textAlign: 'center', mt: 5 }}>
          <Typography
            variant="h6"
            sx={{
              color: 'primary.main',
              fontWeight: 700,
            }}
          >
            Most tools show one part of your business. MyLojic shows the whole picture.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

// ─── INDUSTRY STATS ───────────────────────────────────────────────────────────
// v4.05 - PORTAL-006: "What clarity gives you" — 3 title+body cards, no numbers, no product claims - 2026-03-27
const clarityCards = [
  {
    title: 'Hidden margin loss is common',
    body: "Most SMBs lose 10 to 20% of margin to inefficiencies they don't see.",
  },
  {
    title: 'Decisions are made with incomplete data',
    body: 'Most SMB decisions are made across disconnected systems and partial information.',
  },
  {
    title: 'Time is spent searching, not deciding',
    body: 'Owners spend hours each week pulling numbers instead of acting on them.',
  },
];

const IndustryStatsSection: React.FC = () => (
  <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'secondary.main' }}>
    <Container maxWidth="lg">
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          mb: 5,
          textAlign: 'center',
          color: '#fff',
          fontSize: { xs: '1.25rem', md: '2rem' },
        }}
      >
        What clarity gives you
      </Typography>

      {/* 3-tile row — Box (not Card/Paper) so backdrop-filter blur renders correctly */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
          width: '100%',
        }}
      >
        {clarityCards.map((item) => (
          <Box
            key={item.title}
            sx={{
              background: 'rgba(255, 255, 255, 0.06)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              backgroundColor: 'transparent',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.08)',
              padding: { xs: '28px 24px', md: '40px 32px' },
              flex: 1,
              minWidth: 0,
              textAlign: 'center',
              transition: 'box-shadow 200ms ease, border-color 200ms ease',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.22)',
                boxShadow: '0 12px 40px rgba(0,0,0,0.32), inset 0 1px 0 rgba(255,255,255,0.12)',
              },
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: '1rem', md: '1.1rem' },
                fontWeight: 700,
                color: '#00C9A7',
                lineHeight: 1.4,
                mb: 1.5,
              }}
            >
              {item.title}
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.9rem',
                textAlign: 'center',
                lineHeight: 1.6,
                maxWidth: '220px',
                mx: 'auto',
              }}
            >
              {item.body}
            </Typography>
          </Box>
        ))}
      </Box>
    </Container>
  </Box>
);

// ─── S-04: SOCIAL PROOF (empty slot — renders when content exists) ─────────────
// Set SOCIAL_PROOF to a quote object when content is available.
// Leave null to hide the section entirely.
// Note: `as` cast prevents TypeScript const-narrowing the literal null to `never` in the guard below.
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
const SOCIAL_PROOF = null as null | { quote: string; name: string; title: string; company: string };

const SocialProofSection: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!SOCIAL_PROOF || !sectionRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) analytics.socialProofView('quote'); },
      { threshold: 0.3 }
    );
    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  if (!SOCIAL_PROOF) return null;

  return (
    <Box ref={sectionRef} sx={{ py: { xs: 6, md: 8 }, bgcolor: 'background.default' }}>
      <Container maxWidth="md">
        <Card elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: { xs: 3, md: 5 }, bgcolor: 'background.paper' }}>
          <Typography
            variant="h5"
            sx={{ fontStyle: 'italic', color: 'text.primary', mb: 3, lineHeight: 1.6, fontWeight: 400 }}
          >
            "{SOCIAL_PROOF.quote}"
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
              {SOCIAL_PROOF.name.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{SOCIAL_PROOF.name}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>{SOCIAL_PROOF.title}, {SOCIAL_PROOF.company}</Typography>
            </Box>
          </Box>
        </Card>
      </Container>
    </Box>
  );
};

// ─── S-05: PRICING (behind features.pricing flag) ─────────────────────────────
// v4.10 - STORY-10E-C: fully catalog-driven; no tier-name/price literals; omit elements when catalog absent
// v4.09 - STORY8-STEP3: reads price/limits from /api/plans catalog; hardcoded fallback on error
const PricingSection: React.FC<{ siteMode: string }> = ({ siteMode }) => {
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLDivElement>(null);
  const cta = getPrimaryCTA(siteMode as any);

  // v4.10 - STORY-10E-C: full catalog; filter subscription tiers; find highest sort_order
  const [pricingTier, setPricingTier] = useState<Tier | null>(null);

  useEffect(() => {
    plansService.getPlans().then((tiers) => {
      // findUpgradeTier filters payment_mode==='subscription' and returns highest sort_order
      setPricingTier(findUpgradeTier(tiers));
    }).catch(() => {
      // pricingTier stays null; price section omits elements (no hardcoded fallback)
    });
  }, []);

  useEffect(() => {
    if (!sectionRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) analytics.pricingSectionView(); },
      { threshold: 0.3 }
    );
    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Derived display values — catalog only; elements omitted when value absent
  const cur = pricingTier?.currency?.toUpperCase() || 'USD';
  const fmtCurrency = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: cur, maximumFractionDigits: 0 }).format(n);

  const price = pricingTier ? fmtCurrency(pricingTier.price_monthly) : null;
  const setupFee = pricingTier && pricingTier.setup_fee > 0 ? fmtCurrency(pricingTier.setup_fee) : null;
  const creditMonths = pricingTier?.setup_credit_months ?? null;  // null → omit element
  const questionsLimit = pricingTier
    ? (pricingTier.limits.api_calls_monthly === -1 ? null : pricingTier.limits.api_calls_monthly)
    : null; // null → omit element when catalog absent
  const unlimitedSources = !pricingTier || pricingTier.limits.connections === -1;

  const includes: string[] = [
    unlimitedSources ? 'Unlimited data sources' : `${pricingTier!.limits.connections} data sources`,
    'Unlimited connectors',
    ...(questionsLimit === null && pricingTier
      ? ['Unlimited fully answered questions per month']
      : questionsLimit !== null
        ? [`${questionsLimit.toLocaleString()} fully answered questions per month`]
        : []),
    'No annual contract',
    ...(creditMonths !== null ? [`Setup fee refunded in credits over ${creditMonths} months`] : []),
    'Questions reset each month',
  ];

  const handleCTAClick = () => {
    analytics.ctaClick('hero', siteMode);
    navigate(cta.href);
  };

  return (
    <Box id="pricing" ref={sectionRef} sx={{ py: { xs: 6, md: 10 }, bgcolor: 'background.default' }}>
      <Container maxWidth="sm">
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, mb: 1.5, textAlign: 'center', color: 'text.primary' }}
        >
          Simple pricing
        </Typography>
        <Typography
          variant="body1"
          sx={{ mb: 6, textAlign: 'center', color: 'text.secondary' }}
        >
          One plan. Everything included. No surprises.
        </Typography>

        <Card
          elevation={0}
          sx={{
            border: 2,
            borderColor: 'primary.main',
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          {/* Price header — only rendered when catalog available */}
          {price !== null && (
          <Box sx={{ bgcolor: 'primary.main', p: 4, textAlign: 'center' }}>
            <Typography variant="h2" sx={{ fontWeight: 800, color: '#fff', lineHeight: 1 }}>
              {price}
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.5 }}>
              per month · no annual contract
            </Typography>
          </Box>
          )}

          <CardContent sx={{ p: 4 }}>
            {/* Included items */}
            <Box sx={{ mb: 3 }}>
              {includes.map((item) => (
                <Box key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                  <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 20, mt: 0.1, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Setup fee note — omit if catalog key absent */}
            {setupFee !== null && creditMonths !== null && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                <strong>{setupFee} one-time setup fee</strong>: refunded in full as account credits over {creditMonths} months.
              </Typography>
            </Box>
            )}

            {/* Questions limit note — omit if catalog key absent */}
            {pricingTier !== null && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                {questionsLimit === null
                  ? 'Unlimited questions. Your data stays live.'
                  : `At ${questionsLimit.toLocaleString()} questions, no more questions until the next month. Your data stays live.`}
              </Typography>
            </Box>
            )}

            {/* CTA */}
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleCTAClick}
              sx={{
                bgcolor: 'accent.main',
                color: 'accent.contrastText',
                py: 1.75,
                fontWeight: 700,
                fontSize: '1rem',
                '&:hover': { bgcolor: 'accent.dark' },
              }}
            >
              {cta.text}
            </Button>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

// ─── S-06: FOUNDING STORY TEASER ──────────────────────────────────────────────
const FounderStorySection: React.FC<{ showAboutLink: boolean }> = ({ showAboutLink }) => {
  const navigate = useNavigate();

  const handleAboutClick = () => {
    analytics.aboutClick();
    navigate('/about');
  };

  return (
    <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'background.paper' }}>
      <Container maxWidth="md">
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 4,
            alignItems: { md: 'flex-start' },
          }}
        >
          {/* Founder avatar — v4.02: left-align name/title to avatar left edge - 2026-03-23 */}
          <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
            <Avatar
              sx={{
                width: 96,
                height: 96,
                bgcolor: 'primary.main',
                fontSize: '2rem',
                fontWeight: 700,
              }}
            >
              BM
            </Avatar>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Branko Milojic
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Founder & CEO
            </Typography>
          </Box>

          {/* Quote — v4.02: updated copy + pb to prevent italic descender clipping - 2026-03-23 */}
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontStyle: 'italic',
                color: 'text.primary',
                lineHeight: 1.8,
                fontWeight: 400,
                mb: 2,
                pb: '6px',
                overflow: 'visible',
              }}
            >
              {/* v4.06 - Restore original founder quote (v3.94 text) - 2026-03-27 */}
              "I spent years watching enterprise analytics tools get sold to companies that already had analysts.
              The SMB owners who actually needed answers, running 20-person distributors, managing job sites,
              watching cash flow manually every Friday morning, never saw any of it. Not because the data wasn't
              there. Because extracting it required a team they couldn't afford.
              <br /><br />
              That's why I built MyLojic."
            </Typography>

            {showAboutLink && (
              <Button
                variant="text"
                onClick={handleAboutClick}
                sx={{ color: 'primary.main', fontWeight: 600, p: 0, '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
              >
                Read our story →
              </Button>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

// ─── S-07: FINAL CTA BLOCK ────────────────────────────────────────────────────
// v4.05 - PORTAL-006: new header "Ready to see the full picture?", fixed subtext - 2026-03-27
const FinalCTASection: React.FC<{ siteMode: string; flagsLoaded: boolean }> = ({ siteMode, flagsLoaded }) => {
  const navigate = useNavigate();
  const cta = getPrimaryCTA(siteMode as any);

  const handleCTAClick = () => {
    analytics.ctaClick('final_cta', siteMode);
    navigate(cta.href);
  };

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        bgcolor: 'primary.main',
        textAlign: 'center',
      }}
    >
      <Container maxWidth="md">
        <Typography
          variant="h3"
          sx={{ fontWeight: 800, color: '#fff', mb: 2, fontSize: { xs: '1.8rem', md: '2.5rem' } }}
        >
          Ready to see the full picture?
        </Typography>
        <Typography
          variant="h6"
          sx={{ color: 'rgba(255,255,255,0.8)', mb: 5, fontWeight: 400, maxWidth: 520, mx: 'auto' }}
        >
          Be first to get access.
        </Typography>

        {flagsLoaded && (
          <Button
            variant="contained"
            size="large"
            onClick={handleCTAClick}
            sx={{
              bgcolor: 'accent.main',
              color: 'accent.contrastText',
              py: 2,
              px: 6,
              fontSize: '1.1rem',
              fontWeight: 700,
              boxShadow: 4,
              '&:hover': { bgcolor: 'accent.dark', boxShadow: 6 },
            }}
          >
            {cta.text}
          </Button>
        )}
      </Container>
    </Box>
  );
};

// ─── MAIN LANDING PAGE ────────────────────────────────────────────────────────
interface LandingProps {
  showWaitlistBanner?: boolean;
  detectedCountry?: string;
}

const Landing: React.FC<LandingProps> = ({ showWaitlistBanner = false, detectedCountry = 'US' }) => {
  const { siteMode, features, flagsLoaded } = useFeatureFlags();
  const [bannerVisible, setBannerVisible] = useState(true);

  // X-03: Scroll depth tracking
  useEffect(() => {
    const milestones = [25, 50, 75, 100] as const;
    const fired = new Set<number>();

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;
      const progress = Math.round((scrollTop / docHeight) * 100);
      for (const m of milestones) {
        if (!fired.has(m) && progress >= m) {
          fired.add(m);
          analytics.scrollDepth(m);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Box>
      {/* v4.38 - EXPO-QUALIFIER-01: November data cleaning offer, under the header */}
      <OfferStrip />

      {/* S-01: Hero */}
      <HeroSection siteMode={siteMode} flagsLoaded={flagsLoaded} />

      {/* Non-US waitlist banner — MKTG-02 */}
      {showWaitlistBanner && bannerVisible && (
        <Container maxWidth="sm" sx={{ mt: 3, mb: 1 }}>
          <NonUsWaitlistBanner
            detectedCountry={detectedCountry}
            onDismiss={() => setBannerVisible(false)}
          />
        </Container>
      )}

      {/* AEO-08: What is MyLojic? — AI crawler anchor */}
      <Box component="section" id="what-is-mylojic" sx={{ py: 6, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="h2" sx={{ fontSize: '24px', fontWeight: 700, mb: 2, color: 'text.primary' }}>
            What is MyLojic?
          </Typography>
          <Typography sx={{ fontSize: '16px', lineHeight: 1.6, color: 'text.secondary' }}>
            MyLojic is a profit-discovery platform for owner-operated small businesses. It connects to the tools you already use, cleans your data automatically, and tells you where you are losing money before you even think to ask.
          </Typography>
        </Container>
      </Box>

      {/* S-02: Problem */}
      <ProblemSection />

      {/* S-03: How It Works */}
      <HowItWorksSection />

      {/* Industry Stats — always visible, all modes */}
      <IndustryStatsSection />

      {/* S-04: Social Proof (hidden until content exists) */}
      <SocialProofSection />

      {/* S-05: Pricing (behind features.pricing flag) */}
      {flagsLoaded && features.pricing && (
        <PricingSection siteMode={siteMode} />
      )}

      {/* S-06: Founding Story Teaser */}
      <FounderStorySection showAboutLink={flagsLoaded && features.about} />

      {/* S-07: Final CTA */}
      <FinalCTASection siteMode={siteMode} flagsLoaded={flagsLoaded} />
    </Box>
  );
};

export default Landing;
